import React from 'react';
import {
  Globe, GitBranch, FolderOpen, ArrowRight, Loader2, FileCode2, Clock, AlertTriangle, RotateCcw,
} from 'lucide-react';
import type { AppInfo, Backup, CommitInfo, DeployState, NginxTarget, ServerInfo, ServerStatus } from '../deploy/types';
import {
  LivePipeline,
  LogPanel,
  DeployBtn,
  STATUS_COLOR,
  serverFlag,
} from '../deploy/deployWidgets';

export interface DeployPageProps {
  servers: ServerInfo[];
  apps: AppInfo[];
  passwords: Record<string, string>;
  serverStatus: Record<string, { status: ServerStatus; info: string }>;
  getDS: (key: string) => DeployState;
  patchDeploy: (key: string, patch: Partial<DeployState>) => void;
  setLastOp: React.Dispatch<React.SetStateAction<Record<string, 'deploy' | 'rollback'>>>;
  startDeploy: (url: string, key: string, pw: Record<string, string>, extra?: Record<string, unknown>) => Promise<void>;
  pwPayload: () => Record<string, string>;
  fetchCommits: (appId: string, serverId: string) => Promise<void>;
  commitsLoading: Record<string, boolean>;
  commits: Record<string, CommitInfo[]>;
  commitHash: Record<string, string>;
  setCommitHash: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  setCommitModalKey: React.Dispatch<React.SetStateAction<string | null>>;
  fetchBackups: (appId: string, serverId: string) => Promise<void>;
  backupsLoading: Record<string, boolean>;
  backups: Record<string, Backup[]>;
  selectedBackup: Record<string, string>;
  setSelectedBackup: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  doRollback: (appId: string, serverId: string, stateKey: string) => Promise<void>;
  lastOp: Record<string, 'deploy' | 'rollback'>;
  setNginxTarget: React.Dispatch<React.SetStateAction<NginxTarget | null>>;
}

function appCardStatus(appId: string, servers: ServerInfo[], getDS: (k: string) => DeployState): string {
  const keys = [...servers.map((s) => `${appId}__${s.id}`), `${appId}__all`];
  if (keys.some((k) => getDS(k).status === 'running')) return 'deploying';
  if (keys.some((k) => getDS(k).status === 'error')) return 'failed';
  if (keys.some((k) => getDS(k).status === 'success')) return 'deployed';
  return '';
}

export default function DeployPage(props: DeployPageProps) {
  const {
    servers,
    apps,
    passwords,
    serverStatus,
    getDS,
    patchDeploy,
    setLastOp,
    startDeploy,
    pwPayload,
    fetchCommits,
    commitsLoading,
    commits,
    commitHash,
    setCommitHash,
    setCommitModalKey,
    fetchBackups,
    backupsLoading,
    backups,
    selectedBackup,
    setSelectedBackup,
    doRollback,
    lastOp,
    setNginxTarget,
  } = props;

  return (
    <div className="deploy-hub">
      <div className="dns-shell dash-shell">
        <main className="main dash-main">
          <div className="hub-page-head deploy-hub-head">
            <h1 className="hub-page-h1">Deploy</h1>
            <p className="hub-page-sub">Build, deploy, nginx config, commits, backups, and rollback per application and server.</p>
          </div>

          <div className="apps-list">
            {apps.map((app) => {
              const appServers =
                app.serverIds && app.serverIds.length > 0
                  ? servers.filter((s) => app.serverIds!.includes(s.id))
                  : servers;
              const srvKeys = appServers.map((s) => ({ sv: s, key: `${app.id}__${s.id}` }));
              const allKey = `${app.id}__all`;
              const allDS = getDS(allKey);
              const anyRunning =
                allDS.status === 'running' || srvKeys.some(({ key }) => getDS(key).status === 'running');
              const appMissingPw = appServers.filter((s) => !passwords[s.id]?.trim()).map((s) => s.label);
              const cardSt = appCardStatus(app.id, servers, getDS);

              return (
                <div key={app.id} className={`app-card ${cardSt}`}>
                  <div className="app-head">
                    <div className="app-icon">
                      <Globe size={20} />
                    </div>
                    <div className="app-meta">
                      <h2>{app.name}</h2>
                      <div className="app-tags">
                        {app.url && (
                          <a href={`https://${app.url}`} target="_blank" rel="noreferrer" className="app-url">
                            {app.url}
                          </a>
                        )}
                        {app.gitBranch && (
                          <span className="tag-branch">
                            <GitBranch size={10} />
                            {app.gitBranch}
                          </span>
                        )}
                      </div>
                    </div>
                    <DeployBtn
                      label={srvKeys.length === 1 ? `Deploy to ${appServers[0]?.label}` : `Deploy All (${srvKeys.length})`}
                      onClick={() => {
                        setLastOp((p) => ({ ...p, [allKey]: 'deploy' }));
                        void startDeploy(`/api/deploy/${app.id}/all`, allKey, pwPayload());
                      }}
                      status={allDS.status}
                      disabled={anyRunning || appMissingPw.length > 0}
                    />
                  </div>

                  <div className="app-paths">
                    <div className="path-pill">
                      <FolderOpen size={11} />
                      <span>Source</span>
                      <code>{app.sourceDir}</code>
                    </div>
                    <div className="path-pill">
                      <ArrowRight size={11} />
                      <span>Deploy</span>
                      <code>{app.deployDir}</code>
                    </div>
                  </div>

                  <div className="srv-rows">
                    {srvKeys.map(({ sv, key }) => {
                      const ds = getDS(key);
                      const ss = serverStatus[sv.id] || { status: 'unknown' as ServerStatus };
                      const rbDS = getDS(`rb__${key}`);
                      const isRb = lastOp[key] === 'rollback';
                      const cHash = commitHash[key] || '';
                      const bkList = backups[key] || [];

                      return (
                        <div key={sv.id} className="srv-row-wrap">
                          <div className="srv-row">
                            <div className="srv-id">
                              <div className="srv-dot" style={{ background: STATUS_COLOR[ss.status] }} />
                              <span className="srv-flag">{serverFlag(sv.label)}</span>
                              <div className="srv-names">
                                <span className="srv-label">{sv.label}</span>
                                <span className="srv-host">{sv.host}</span>
                              </div>
                              {ss.status === 'online' && <span className="srv-status-tag online">Online</span>}
                              {ss.status === 'offline' && <span className="srv-status-tag offline">Offline</span>}
                            </div>

                            <div className="srv-actions">
                              <div className="cluster">
                                <div className="cluster-lbl">Deploy</div>
                                <div className="cluster-row">
                                  <DeployBtn
                                    label="Deploy"
                                    onClick={() => {
                                      setLastOp((p) => ({ ...p, [key]: 'deploy' }));
                                      void startDeploy(
                                        `/api/deploy/${app.id}/${sv.id}`,
                                        key,
                                        pwPayload(),
                                        cHash ? { commitHash: cHash } : {},
                                      );
                                    }}
                                    status={ds.status}
                                    disabled={anyRunning || !passwords[sv.id]?.trim()}
                                  />

                                  <button
                                    type="button"
                                    className={`btn btn-ghost btn-sm ${cHash ? 'active' : ''}`}
                                    onClick={async () => {
                                      if (!commits[key]) {
                                        await fetchCommits(app.id, sv.id);
                                      }
                                      setCommitModalKey(key);
                                    }}
                                    disabled={!passwords[sv.id]?.trim() || commitsLoading[key]}
                                  >
                                    {commitsLoading[key] ? <Loader2 size={12} className="spin" /> : <GitBranch size={12} />}
                                    {cHash ? <span className="sha-badge">{cHash.slice(0, 7)}</span> : 'Commits'}
                                  </button>
                                  {cHash && (
                                    <button
                                      type="button"
                                      className="sha-x"
                                      title="Clear commit"
                                      onClick={() => setCommitHash((p) => ({ ...p, [key]: '' }))}
                                    >
                                      ×
                                    </button>
                                  )}
                                </div>
                              </div>

                              <div className="cluster-sep" />

                              <div className="cluster">
                                <div className="cluster-lbl">Nginx</div>
                                <div className="cluster-row">
                                  <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() =>
                                      setNginxTarget({
                                        appId: app.id,
                                        serverId: sv.id,
                                        appName: app.name,
                                        serverLabel: sv.label,
                                        nginxSite: app.nginxSite || '',
                                        maintenancePath: app.maintenancePath || '',
                                      })
                                    }
                                    disabled={!passwords[sv.id]?.trim()}
                                    title="View / edit nginx sites-enabled config"
                                  >
                                    <FileCode2 size={12} /> Nginx Config
                                  </button>
                                </div>
                              </div>

                              <div className="cluster-sep" />

                              <div className="cluster">
                                <div className="cluster-lbl">Rollback</div>
                                <div className="cluster-row">
                                  <button
                                    type="button"
                                    className="btn btn-ghost btn-sm"
                                    onClick={() => void fetchBackups(app.id, sv.id)}
                                    disabled={!passwords[sv.id]?.trim() || backupsLoading[key]}
                                  >
                                    {backupsLoading[key] ? <Loader2 size={12} className="spin" /> : <Clock size={12} />}
                                    Backups
                                  </button>

                                  {backups[key] &&
                                    (backups[key].length === 0 ? (
                                      <span className="no-items">
                                        <AlertTriangle size={11} /> No backups yet
                                      </span>
                                    ) : (
                                      <select
                                        className="commit-select"
                                        value={selectedBackup[key] || ''}
                                        onChange={(e) => setSelectedBackup((p) => ({ ...p, [key]: e.target.value }))}
                                        disabled={rbDS.status === 'running'}
                                      >
                                        <option value="">Latest backup</option>
                                        {bkList.map((b, i) => (
                                          <option key={b.file} value={b.file}>
                                            Backup {bkList.length - i} — {b.label}
                                          </option>
                                        ))}
                                      </select>
                                    ))}

                                  <button
                                    type="button"
                                    className="btn btn-rollback btn-sm"
                                    onClick={() => void doRollback(app.id, sv.id, key)}
                                    disabled={anyRunning || rbDS.status === 'running' || !passwords[sv.id]?.trim()}
                                  >
                                    {rbDS.status === 'running' ? (
                                      <>
                                        <Loader2 size={12} className="spin" /> Rolling back…
                                      </>
                                    ) : (
                                      <>
                                        <RotateCcw size={12} /> Rollback
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>

                          {!isRb ? (
                            <LivePipeline logs={ds.logs} status={ds.status} label={`Deploying to ${sv.label}`} />
                          ) : (
                            <LivePipeline
                              logs={rbDS.logs}
                              status={rbDS.status}
                              label={`Rolling back ${sv.label}`}
                              mode="rollback"
                            />
                          )}

                          {!isRb ? (
                            <LogPanel
                              logs={ds.logs}
                              open={ds.logsOpen}
                              onToggle={() => patchDeploy(key, { logsOpen: !ds.logsOpen })}
                              title={`Deploy — ${sv.label}`}
                            />
                          ) : (
                            <LogPanel
                              logs={rbDS.logs}
                              open={rbDS.logsOpen}
                              onToggle={() => patchDeploy(`rb__${key}`, { logsOpen: !rbDS.logsOpen })}
                              title={`Rollback — ${sv.label}`}
                            />
                          )}
                        </div>
                      );
                    })}
                  </div>

                  <LivePipeline
                    logs={allDS.logs}
                    status={allDS.status}
                    label={`Deploying to ${appServers.map((s) => s.label).join(', ')}`}
                    serverCount={srvKeys.length}
                  />
                  <LogPanel
                    logs={allDS.logs}
                    open={allDS.logsOpen}
                    onToggle={() => patchDeploy(allKey, { logsOpen: !allDS.logsOpen })}
                    title="All servers — combined"
                  />
                </div>
              );
            })}
          </div>
        </main>
      </div>
    </div>
  );
}
